import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { catchError, tap } from 'rxjs/operators';
import { throwError, Observable/*, of*/ } from 'rxjs';
import { Documents, headers_skip_token, SKIP_LOADER_HEADER } from './utility.service';
import { StorageService } from './storage.service';

declare interface TokenData {
	scope: 'default';
	token_type: 'Bearer';
	expires_in: number;
	refresh_token: string;
	access_token: string;
}

declare const AppConf: any;

@Injectable({
	providedIn: 'root'
})

export class BackendService {
	constructor(
		private http: HttpClient,
		private storageService: StorageService
	) { }

	/**
	 * getAccessToken
	 * @param code:string - code as found in the querystring
	 * @param redirect_uri:string - redirect uri
	 * @returns  - the Access Token String
	 */
	private readonly getAuthorizationHeaderValue =
		() => `Basic ${btoa(`${AppConf.clientId}:${AppConf.secretId}`)}`

	getAccessToken(code: string, redirect_uri: string): Observable<TokenData> {
		return this.http.post<TokenData>(
			`${AppConf.apiUrl}/token`,
			new HttpParams()
				.set('code', code)
				.set('grant_type', 'authorization_code')
				.set('redirect_uri', redirect_uri),
			{
				headers: headers_skip_token
					.append('Authorization', this.getAuthorizationHeaderValue())
			})
			.pipe(
				tap(
					data => {
						// console.log('fetched Access Token');
						let refreshToken = data.refresh_token;
						this.storageService.setToSessionStorage('accessToken', data.access_token);
						setInterval(() => {
							this.refreshAccessToken(refreshToken)
								.subscribe(response => {
									refreshToken = response.refresh_token;
									this.storageService.setToSessionStorage('accessToken', response.access_token);
								},
									error => {
										console.error(error);
									}
								);
						},
							data.expires_in * 1000);
					}
				),
				catchError(error => throwError(error))
			);
	}

	/**
	 * refreshAccessToken
	 * @param refreshToken - refresh access code as returned by getAccessToken
	 * @returns  - the Access Token String
	 */

	refreshAccessToken(refreshToken: string): Observable<TokenData> {
		const params = new HttpParams()
			.set('refresh_token', refreshToken)
			.set('grant_type', 'refresh_token');
		return this.http.post<TokenData>(
			`${AppConf.apiUrl}/token`,
			params,
			{
				headers: headers_skip_token
					.append(SKIP_LOADER_HEADER, '')
					.append('Authorization', this.getAuthorizationHeaderValue())
			})
			.pipe(
				tap(
					() => { console.log('Refreshed Access Token'); }
				),
				catchError(error => throwError(error))
			);
	}

	/**
	* getCorrelationId
	*/
	getCorrelationId(): Observable<string> {
		const cid = this.storageService.getFromSessionStorage('correlationId');
		if (cid) {
			return Observable.create(obs => {
				obs.next(cid);
			});
		}
		else {
			return this.http.get(
				`${AppConf.apiUrl}${environment.server_correlationId}`,
				{
					responseType: 'text',
					headers: headers_skip_token
				})
				.pipe(
					tap(
						(id: string) => {
							this.storageService.setToSessionStorage('correlationId', id);
							console.log('fetched getCorrelationId', id);
						}
					),
					catchError(error => throwError(error))
				);
		}
	}

	/**
	* iniziaProcesso
	* @param qs - Querystring
	*/
	iniziaProcesso(qs: string) {
		return this.http.post<{
			codiceEsito: "OK" | "NOT_OK";
			token: string
		}>(
			`${AppConf.apiUrl}${environment.path}/iniziaProcesso`,
			{
				qs
			})
			.pipe(
				tap(
					() => console.log('fetched iniziaProcesso')
				),
				catchError(error => throwError(error))
			);
	}

	/**
	 * recuperaFileCaricati
	 */
	recuperaFileCaricati() {
		return this.http.post<Documents>(
			`${AppConf.apiUrl}${environment.path}/recuperaFileCaricati`,
			{})
			.pipe(
				tap(
					() => console.log('fetched recuperaFileCaricati')
				),
				catchError(error => throwError(error))
			);
	}

	/**
	 * visualizzaDocumento
	 */
	visualizzaDocumento(codiceTipoDocumento: string, numeroPagina: number) {
		return this.http.get<any>(
			`${AppConf.apiUrl}${environment.path}/visualizzaDocumento/${this.storageService.getFromSessionStorage('token')}/${codiceTipoDocumento}/${numeroPagina}`,
			{
				responseType: 'blob' as 'json'
			})
			.pipe(
				tap(
					() => console.log('fetched visualizzaDocumento')
				),
				catchError(error => throwError(error))
			);
	}

	/**
	 * caricaDocumento
	 */
	caricaDocumento(codiceTipoDocumento: string, file: File) {
		const formData: FormData = new FormData();
		// for (let i = 0; i < files.length; i++) {
		formData.append('fileContent', file);
		formData.append('codiceTipoDocumento', codiceTipoDocumento);
		// }
		return this.http.post<{ numeroPagina: number }>(
			`${AppConf.apiUrl}${environment.path}/caricaDocumento`,
			formData
		)
			.pipe(
				tap(
					() => console.log('fetched caricaDocumento')
				)/*,
				catchError(error => throwError(error))*/
			);
	}


	/**
	 * eliminaDocumento
	 */
	eliminaDocumento(codiceTipoDocumento: string, numeroPagina: number) {
		return this.http.post<any>(
			`${AppConf.apiUrl}${environment.path}/eliminaDocumento`,
			{
				codiceTipoDocumento,
				numeroPagina
			})
			.pipe(
				tap(
					() => console.log('fetched eliminaDocumento')
				)/*,
				catchError(error => throwError(error))*/
			);
	}


	/**
	 * confermaDocumenti
	 */
	confermaDocumenti() {
		return this.http.post<any>(
			`${AppConf.apiUrl}${environment.path}/confermaDocumenti`,
			{})
			.pipe(
				tap(
					() => console.log('fetched confermaDocumenti')
				)/*,
				catchError(error => throwError(error))*/
			);
	}

}
